import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { Project, ProjectStatus } from '../../types/Project';
import { projectService } from '../../services/projectService';
import { taskService, Task, TaskFormData } from '../../services/taskService';
import { tagService, Tag } from '../../services/tagService';
import TaskRow from '../tasks/TaskRow';
import TaskForm from '../tasks/TaskForm';
import PageHeader from '../common/PageHeader';
import './ProjectDetail.css';
import './ProjectList.css';

interface ProjectDetailProps {
  user: User | null;
  onToggleSidebar?: () => void;
  onClose?: () => void;
}

const ProjectDetail: React.FC<ProjectDetailProps> = ({ user, onToggleSidebar }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks'>('overview');

  // Project form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<ProjectStatus>(ProjectStatus.NOT_STARTED);
  const [startDate, setStartDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [progressPercentage, setProgressPercentage] = useState('0');

  // Tasks state
  const [projectTasks, setProjectTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [taskTags, setTaskTags] = useState<{ [taskId: number]: string[] }>({});
  const [showNewTaskForm, setShowNewTaskForm] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [showDatePickerModal, setShowDatePickerModal] = useState(false);
  const [taskToMove, setTaskToMove] = useState<Task | null>(null);
  const [selectedMoveDate, setSelectedMoveDate] = useState('');

  // Load project data
  useEffect(() => {
    if (user && id) {
      loadProject();
      loadProjectTasks();
      loadAvailableTags();
    }
  }, [user, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle clicks outside menu dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // Helper function to format dates for input fields
  const formatDateForInput = (dateString: string | undefined | null): string => {
    if (!dateString) return '';
    
    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const loadProject = async () => {
    if (!user || !id) return;

    setLoading(true);
    try {
      const response = await projectService.getProjectById(id);

      if (response.success && response.project) {
        const convertedProject = projectService.convertDatabaseProject(response.project);
        setProject(convertedProject);

        // Populate form fields
        setName(convertedProject.name);
        setDescription(convertedProject.description || '');
        setStatus(convertedProject.status);
        setStartDate(formatDateForInput(convertedProject.start_date));
        setTargetDate(formatDateForInput(convertedProject.target_date));
        setProgressPercentage(convertedProject.progress_percentage.toString());

        setError(null);
      } else {
        setError('Project not found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load project');
      console.error('Error loading project:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadProjectTasks = async () => {
    if (!id) return;

    setTasksLoading(true);
    try {
      const response = await taskService.getProjectTasks(id);
      if (response.success && response.tasks) {
        setProjectTasks(response.tasks);
        
        // Extract tags from tasks
        const tagsMap: { [taskId: number]: string[] } = {};
        response.tasks.forEach((task: Task) => {
          tagsMap[task.id] = task.tags || [];
        });
        setTaskTags(tagsMap);
      }
    } catch (err: any) {
      console.error('Error loading project tasks:', err);
    } finally {
      setTasksLoading(false);
    }
  };

  const loadAvailableTags = async () => {
    try {
      const response = await tagService.getAllTags();
      if (response.success && response.tags) {
        setAvailableTags(response.tags);
      }
    } catch (error) {
      console.error('Error loading available tags:', error);
    }
  };

  const handleSave = async () => {
    if (!user || !project) return;

    if (!name.trim()) {
      setError('Project name is required');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const updateData: any = {
        id: project.id,
        name: name.trim(),
        description: description.trim() || undefined,
        status,
        start_date: startDate || undefined,
        target_date: targetDate || undefined,
        progress_percentage: progressPercentage ? parseInt(progressPercentage) : undefined
      };

      const result = await projectService.updateProject(updateData);

      if (result.success) {
        setSuccessMessage('Project updated successfully!');
        setIsEditing(false);
        await loadProject();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error('Failed to update project');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update project');
      console.error('Error updating project:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (project) {
      setName(project.name);
      setDescription(project.description || '');
      setStatus(project.status);
      setStartDate(formatDateForInput(project.start_date));
      setTargetDate(formatDateForInput(project.target_date));
      setProgressPercentage(project.progress_percentage.toString());
      setIsEditing(false);
      setError(null);
    }
  };
  
  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleDelete = async () => {
    if (!user || !project) return;

    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const result = await projectService.deleteProject(project.id);

      if (result.success) {
        navigate('/projects', { replace: true });
      } else {
        throw new Error('Failed to delete project');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete project');
      console.error('Error deleting project:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = () => {
    // Field change tracking removed since we're using explicit edit mode
  };

  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.NOT_STARTED: return '#6c757d';
      case ProjectStatus.IN_PROGRESS: return '#007bff';
      case ProjectStatus.COMPLETED: return '#28a745';
      case ProjectStatus.ON_HOLD: return '#ffc107';
      case ProjectStatus.CANCELLED: return '#dc3545';
      default: return '#6c757d';
    }
  };

  const formatStatus = (status: ProjectStatus) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No date set';
    return new Date(dateString).toLocaleDateString();
  };

  // Get today's date in YYYY-MM-DD format
  const getToday = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate tomorrow's date (using local date to avoid timezone issues)
  const calculateTomorrow = (assignedDate?: string): string => {
    const baseDate = assignedDate ? assignedDate : getToday();
    const [year, month, day] = baseDate.split('-').map(Number);
    const tomorrow = new Date(year, month - 1, day);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowYear = tomorrow.getFullYear();
    const tomorrowMonth = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const tomorrowDay = String(tomorrow.getDate()).padStart(2, '0');
    return `${tomorrowYear}-${tomorrowMonth}-${tomorrowDay}`;
  };

  // Calculate next week (same day, next week) - using local date to avoid timezone issues
  const calculateNextWeek = (currentDate: string): string => {
    const [year, month, day] = currentDate.split('-').map(Number);
    const nextWeek = new Date(year, month - 1, day);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const nextWeekYear = nextWeek.getFullYear();
    const nextWeekMonth = String(nextWeek.getMonth() + 1).padStart(2, '0');
    const nextWeekDay = String(nextWeek.getDate()).padStart(2, '0');
    return `${nextWeekYear}-${nextWeekMonth}-${nextWeekDay}`;
  };

  // Task management handlers
  const handleCreateTask = async (taskData: TaskFormData & { tag_names?: string[] }) => {
    if (!user || !id) return;

    setCreatingTask(true);
    setError(null);
    try {
      const response = await taskService.createTask(user.id.toString(), {
        ...taskData,
        project_id: parseInt(id)
      });

      if (response.success) {
        setShowNewTaskForm(false);
        await loadProjectTasks();
        setSuccessMessage('Task created successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(response.message || 'Failed to create task');
      }
    } catch (err) {
      console.error('Error creating task:', err);
      setError('Failed to create task');
    } finally {
      setCreatingTask(false);
    }
  };

  const handleToggleTaskStatus = async (task: Task) => {
    try {
      const newStatus = task.status === 'completed' ? 'todo' : 'completed';
      const response = await taskService.updateTask({
        id: task.id.toString(),
        status: newStatus
      });

      if (response.success) {
        await loadProjectTasks();
      } else {
        setError(response.message || 'Failed to update task');
      }
    } catch (err) {
      console.error('Error updating task:', err);
      setError('Failed to update task');
    }
  };

  const handleTaskClick = (taskId: number) => {
    navigate(`/tasks/${taskId}`);
  };

  const handleTaskMenuAction = async (task: Task, action: 'today' | 'tomorrow' | 'next-week' | 'backlog' | 'custom-date' | 'delete' | 'log-time') => {
    if (!user) return;

    try {
      if (action === 'delete') {
        if (!window.confirm('Delete this task?')) return;
        
        const response = await taskService.deleteTask(task.id.toString());
        
        if (response.success) {
          await loadProjectTasks();
          setSuccessMessage('Task deleted successfully!');
          setTimeout(() => setSuccessMessage(null), 3000);
        } else {
          setError('Failed to delete task. Please try again.');
        }
      } else if (action === 'log-time') {
        // Navigate to task detail page for time logging
        navigate(`/tasks/${task.id}`);
      } else {
        let newDate: string | null = null;
        const baseDate = task.assigned_date || getToday();
        
        if (action === 'today') {
          newDate = getToday();
        } else if (action === 'tomorrow') {
          newDate = calculateTomorrow(baseDate);
        } else if (action === 'next-week') {
          newDate = calculateNextWeek(baseDate);
        } else if (action === 'backlog') {
          newDate = null; // Remove assigned_date
        } else if (action === 'custom-date') {
          // Open date picker modal
          setTaskToMove(task);
          // Default to task's assigned_date if it exists, otherwise today
          const defaultDate = task.assigned_date || getToday();
          setSelectedMoveDate(defaultDate);
          setShowDatePickerModal(true);
          return; // Exit early, will handle update in modal
        }
        
        const updateData: any = {
          id: task.id.toString()
        };
        
        if (action === 'backlog') {
          updateData.assigned_date = null;
        } else if (newDate) {
          updateData.assigned_date = newDate;
        }
        
        const response = await taskService.updateTask(updateData);
        
        if (response.success) {
          await loadProjectTasks();
          
          const actionMessage = action === 'backlog' 
            ? 'Task moved to backlog' 
            : action === 'today'
            ? `Task assigned to today (${newDate})`
            : action === 'tomorrow'
            ? `Task rescheduled to tomorrow (${newDate})`
            : `Task rescheduled to next week (${newDate})`;
          
          setSuccessMessage(actionMessage);
          setTimeout(() => setSuccessMessage(null), 3000);
        } else {
          setError(`Failed to update task: ${response.message || 'Please try again.'}`);
        }
      }
    } catch (error) {
      console.error('Error handling task menu action:', error);
      setError(`Failed to update task: ${error instanceof Error ? error.message : 'Please try again.'}`);
    }
  };

  // Handle moving task to custom date
  const handleMoveTaskToDate = async () => {
    if (!user || !taskToMove || !selectedMoveDate) return;

    try {
      const updateData: any = {
        id: taskToMove.id.toString(),
        assigned_date: selectedMoveDate
      };

      const response = await taskService.updateTask(updateData);

      if (response.success) {
        await loadProjectTasks();
        setShowDatePickerModal(false);
        setTaskToMove(null);
        setSuccessMessage(`Task moved to ${formatDate(selectedMoveDate)}`);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(`Failed to move task: ${response.message || 'Please try again.'}`);
      }
    } catch (error) {
      console.error('Error moving task to date:', error);
      setError(`Failed to move task: ${error instanceof Error ? error.message : 'Please try again.'}`);
    }
  };

  // Cancel date picker modal
  const cancelDatePicker = () => {
    setShowDatePickerModal(false);
    setTaskToMove(null);
    setSelectedMoveDate('');
  };

  const handleUpdateTaskStatus = async (task: Task, newStatus: 'todo' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled') => {
    try {
      const response = await taskService.updateTask({
        id: task.id.toString(),
        status: newStatus
      });

      if (response.success) {
        await loadProjectTasks();
      } else {
        setError(response.message || 'Failed to update task');
      }
    } catch (err) {
      console.error('Error updating task:', err);
      setError('Failed to update task');
    }
  };

  if (loading) {
    return (
      <div className="project-detail">
        <PageHeader
          onToggleSidebar={onToggleSidebar || (() => {})}
          centerContent={<h1 className="project-detail-header-title">Loading...</h1>}
        />
        <div className="project-detail-loading">
          <div className="project-list-loading-spinner"></div>
          <p>Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="project-detail">
        <PageHeader
          onToggleSidebar={onToggleSidebar || (() => {})}
          centerContent={<h1 className="project-detail-header-title">Project Not Found</h1>}
        />
        <div className="project-detail-error">
          <p>{error || 'Project not found'}</p>
          <button 
            className="project-detail-back-btn"
            onClick={() => navigate('/projects')}
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="project-detail">
      <PageHeader
        onToggleSidebar={onToggleSidebar || (() => {})}
        centerContent={<h1 className="project-detail-header-title">{project.name}</h1>}
        rightActions={
          <div className="project-detail-menu-container" ref={menuRef}>
            <button
              className="project-detail-menu-btn"
              onClick={() => setShowMenu(!showMenu)}
              title="Menu"
            >
              ⋮
            </button>
            {showMenu && (
              <div className="project-detail-menu-dropdown">
                <button
                  className="project-detail-menu-item"
                  onClick={() => {
                    navigate(`/projects/${project.id}/edit`);
                    setShowMenu(false);
                  }}
                >
                  Edit Project
                </button>
                <div className="project-detail-menu-separator"></div>
                <button
                  className="project-detail-menu-item delete"
                  onClick={() => {
                    handleDelete();
                    setShowMenu(false);
                  }}
                >
                  Delete Project
                </button>
              </div>
            )}
          </div>
        }
      />

      {error && (
        <div className="project-detail-error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="project-detail-success-banner">
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      <div className="project-detail-content">
        {/* Tab Navigation */}
        <div className="project-detail-tabs">
          <button
            className={`project-detail-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`project-detail-tab ${activeTab === 'tasks' ? 'active' : ''}`}
            onClick={() => setActiveTab('tasks')}
          >
            Tasks ({projectTasks.length})
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="project-detail-overview">
            {!isEditing ? (
              // Read-only view
              <div className="project-detail-view">
                <div className="project-detail-view-header">
                  <h2 className="project-detail-section-title">Project Information</h2>
                  <button
                    className="project-detail-edit-btn"
                    onClick={handleEdit}
                  >
                    Edit
                  </button>
                </div>

                <div className="project-detail-info-grid">
                  <div className="project-detail-info-item">
                    <label className="project-detail-info-label">Name</label>
                    <div className="project-detail-info-value">{project.name}</div>
                  </div>

                  <div className="project-detail-info-item">
                    <label className="project-detail-info-label">Status</label>
                    <div className="project-detail-info-value">
                      <span 
                        className="project-detail-status-badge"
                        style={{ 
                          backgroundColor: getStatusColor(project.status),
                          color: '#ffffff'
                        }}
                      >
                        {formatStatus(project.status)}
                      </span>
                    </div>
                  </div>

                  <div className="project-detail-info-item full-width">
                    <label className="project-detail-info-label">Description</label>
                    <div className="project-detail-info-value">
                      {project.description || 'No description'}
                    </div>
                  </div>

                  <div className="project-detail-info-item">
                    <label className="project-detail-info-label">Start Date</label>
                    <div className="project-detail-info-value">
                      {formatDate(project.start_date)}
                    </div>
                  </div>

                  <div className="project-detail-info-item">
                    <label className="project-detail-info-label">Target Date</label>
                    <div className="project-detail-info-value">
                      {formatDate(project.target_date)}
                    </div>
                  </div>

                  <div className="project-detail-info-item full-width">
                    <label className="project-detail-info-label">Progress</label>
                    <div className="project-detail-progress-display">
                      <div className="project-list-progress-bar">
                        <div 
                          className="project-list-progress-fill"
                          style={{ width: `${project.progress_percentage}%` }}
                        ></div>
                      </div>
                      <span className="project-detail-progress-text">{project.progress_percentage}%</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Edit mode
              <div className="project-detail-edit">
                <div className="project-detail-view-header">
                  <h2 className="project-detail-section-title">Edit Project</h2>
                </div>

                <div className="project-detail-form">
                  <div className="project-detail-field">
                    <label className="project-detail-label">Name</label>
                    <input
                      type="text"
                      className="project-detail-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Project name"
                    />
                  </div>

                  <div className="project-detail-field">
                    <label className="project-detail-label">Status</label>
                    <select
                      className="project-detail-select"
                      value={status}
                      onChange={(e) => setStatus(e.target.value as ProjectStatus)}
                    >
                      {Object.values(ProjectStatus).map((s) => (
                        <option key={s} value={s}>
                          {formatStatus(s)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="project-detail-field">
                    <label className="project-detail-label">Description</label>
                    <textarea
                      className="project-detail-textarea"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Project description..."
                      rows={4}
                    />
                  </div>

                  <div className="project-detail-field-row">
                    <div className="project-detail-field">
                      <label className="project-detail-label">Start Date</label>
                      <input
                        type="date"
                        className="project-detail-input"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>

                    <div className="project-detail-field">
                      <label className="project-detail-label">Target Date</label>
                      <input
                        type="date"
                        className="project-detail-input"
                        value={targetDate}
                        onChange={(e) => setTargetDate(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="project-detail-field">
                    <label className="project-detail-label">Progress</label>
                    <div className="project-detail-progress-container">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        className="project-detail-progress-slider"
                        value={progressPercentage}
                        onChange={(e) => setProgressPercentage(e.target.value)}
                      />
                      <span className="project-detail-progress-text">{progressPercentage}%</span>
                    </div>
                    <div className="project-list-progress-bar">
                      <div 
                        className="project-list-progress-fill"
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="project-detail-actions">
                    <button
                      className="project-detail-save-btn"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      className="project-detail-cancel-btn"
                      onClick={handleCancel}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div className="project-detail-tasks">
            <div className="project-detail-tasks-header">
              <h2 className="project-detail-section-title">Tasks</h2>
              <button
                className="project-detail-add-task-btn"
                onClick={() => setShowNewTaskForm(!showNewTaskForm)}
              >
                + Add Task
              </button>
            </div>

            {showNewTaskForm && (
              <div className="project-detail-task-form-container">
                <TaskForm
                  onSubmit={handleCreateTask}
                  onCancel={() => setShowNewTaskForm(false)}
                  submitButtonText="Create Task"
                  cancelButtonText="Cancel"
                  isLoading={creatingTask}
                  userId={user?.id}
                  initialData={{
                    title: '',
                    description: '',
                    status: 'todo',
                    due_date: '',
                    assigned_date: '',
                    project_id: project ? Number(project.id) : undefined
                  }}
                />
              </div>
            )}

            {tasksLoading ? (
              <div className="project-detail-tasks-loading">
                <div className="project-list-loading-spinner"></div>
                <p>Loading tasks...</p>
              </div>
            ) : projectTasks.length === 0 ? (
              <div className="project-detail-tasks-empty">
                <p>No tasks yet. Create your first task to get started!</p>
              </div>
            ) : (
              <div className="project-detail-tasks-list">
                {projectTasks.map((task) => (
                  <div key={task.id} className="project-detail-task-item">
                    <TaskRow
                      task={task}
                      onToggleStatus={handleToggleTaskStatus}
                      onTaskClick={handleTaskClick}
                      onMenuAction={handleTaskMenuAction}
                      taskTags={taskTags[task.id] || []}
                      availableTags={availableTags}
                      showMenu={true}
                      showStatusToggle={true}
                    />
                    <div className="project-detail-task-status-selector">
                      <select
                        className="project-detail-task-status-select"
                        value={task.status}
                        onChange={(e) => handleUpdateTaskStatus(task, e.target.value as any)}
                      >
                        <option value="todo">Todo</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="on_hold">On Hold</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Date Picker Modal */}
      {showDatePickerModal && taskToMove && (
        <div className="project-detail-modal-overlay" onClick={cancelDatePicker}>
          <div className="project-detail-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="project-detail-modal-title">Move Task to Date</h3>
            {taskToMove && (
              <div style={{ marginBottom: '1rem', color: '#666666', fontSize: '0.875rem' }}>
                {taskToMove.title}
              </div>
            )}
            <div className="project-detail-modal-field">
              <label className="project-detail-label">Select Date</label>
              <input
                type="date"
                className="project-detail-input"
                value={selectedMoveDate}
                onChange={(e) => setSelectedMoveDate(e.target.value)}
              />
            </div>
            <div className="project-detail-modal-actions">
              <button
                className="project-detail-save-btn"
                onClick={handleMoveTaskToDate}
                disabled={!selectedMoveDate}
              >
                Move Task
              </button>
              <button
                className="project-detail-cancel-btn"
                onClick={cancelDatePicker}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;

